import { createContext, useContext, useEffect, useState } from 'react'
import { api } from '../utils/api'
import { AuthContext } from './AuthContext'

// Types
import { acervoPessoalTypes } from '../types/getData/acervoPessoal'
import { homeTypes } from '../types/getData/home'
import { perfilTypes } from '../types/getData/perfil'
import { treinamentoTypes } from '../types/getData/habitualidade/treinamento'
import { competicaoTypes } from '../types/getData/habitualidade/competicao'
import { historicoTypes } from '../types/getData/historico'
import { financeiroTypes } from '../types/getData/financeiro'

const initialValues = {
  acervoPessoal: acervoPessoalTypes,
  home: homeTypes,
  perfil: perfilTypes,
  'habitualidade/treinamento': treinamentoTypes,
  'habitualidade/competicao': competicaoTypes,
  historico: historicoTypes,
  financeiro: financeiroTypes,
}
const GetDataContext = createContext(initialValues)

const GetDataProvider = ({ children }) => {
  const { user, setUser } = useContext(AuthContext)
  const [data, setData] = useState(initialValues)
  const [count, setCount] = useState(false)
  const [expiredDocuments, setExpiredDocuments] = useState([])
  const [expiredDocumentsLoading, setExpiredDocumentsLoading] = useState(false)
  const [expiredDocumentsError, setExpiredDocumentsError] = useState(null)

  const getData = async (dataKey) => {
    try {
      const response = await fetch(`${api}${dataKey}/getData`, {
        method: 'POST',
        body: JSON.stringify({
          banco: user.clube.banco,
          clubeID: user.clube.clubeID,
          pessoaID: user.pessoa.pessoaID,
          usuarioID: user.pessoa.usuarioID,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      })
      const res = await response.json()

      if (!response.ok || res.success === false) {
        throw new Error(res.error || 'Não foi possível carregar os dados.')
      }

      const responseData = res.success === true ? res.data : res
      setData((prevData) => ({
        ...prevData,
        [dataKey]: responseData,
      }))

      if (dataKey === 'perfil') {
        const formatData = {
          ...user,
          pessoa: {
            ...user.pessoa,
            foto: responseData?.data?.imagem,
          },
        }
        setUser(formatData)
        localStorage.setItem('@sisarma-socio', JSON.stringify(formatData))
      }
      if (dataKey === 'historico') {
        return responseData
      }
    } catch (error) {
      console.error(error)
    }
  }

  const getExpiredDocuments = async () => {
    try {
      setExpiredDocumentsLoading(true)
      setExpiredDocumentsError(null)

      const response = await fetch(`${api}documentosVencidos/getData`, {
        method: 'POST',
        body: JSON.stringify({
          banco: user.clube.banco,
          clubeID: user.clube.clubeID,
          usuarioID: user.pessoa.usuarioID,
          pessoaID: user.pessoa.pessoaID,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      })
      const res = await response.json()

      if (!response.ok || !res.success) {
        throw new Error(
          res.error || 'Não foi possível carregar os vencimentos de documentos.'
        )
      }

      setExpiredDocuments(res.data?.documents || [])
    } catch (error) {
      setExpiredDocuments([])
      setExpiredDocumentsError(
        error.message || 'Não foi possível carregar os vencimentos de documentos.'
      )
    } finally {
      setExpiredDocumentsLoading(false)
    }
  }

  useEffect(() => {
    if (!user) {
      setCount(false)
      setExpiredDocuments([])
      setExpiredDocumentsError(null)
      return
    }

    if (user && user.clube && user.pessoa && !count) {
      getData('home')
      getData('acervoPessoal')
      getData('perfil')
      getData('habitualidade/treinamento')
      getData('habitualidade/competicao')
      getData('historico')
      getData('financeiro')
      getExpiredDocuments()
      setCount(true)
    }
  }, [user, count])

  const values = {
    data,
    getDataAcervoPessoal: () => getData('acervoPessoal'),
    getDataInicio: () => getData('home'),
    getDataPerfil: () => getData('perfil'),
    getDataTreinamento: () => getData('habitualidade/treinamento'),
    getDataCompeticao: () => getData('habitualidade/competicao'),
    getDataHistorico: () => getData('historico'),
    getDataFinanceiro: () => getData('financeiro'),
    expiredDocuments,
    expiredDocumentsLoading,
    expiredDocumentsError,
    getExpiredDocuments,
  }

  return (
    <GetDataContext.Provider value={values}>{children}</GetDataContext.Provider>
  )
}

const useGetData = () => {
  const context = useContext(GetDataContext)
  if (context === undefined) {
    throw new Error('useGetData must be used within a GetDataProvider')
  }
  return context
}

export { GetDataContext, GetDataProvider, useGetData }
